import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import {
  clearAdminSession, fetchSessionStatus, isSessionRejected, readAdminSession, signOut, storeAdminSession,
  type AdminSession
} from "../../services/adminSession";
import { useI18n } from "../../i18n/localization";
import { decideAdminGate, msUntilExpiry } from "./adminGate";
import { AdminLogin } from "./AdminLogin";

interface AdminSessionContextValue {
  session: AdminSession;
  /** Swap in a freshly issued session (after a password change). */
  replaceSession: (next: AdminSession) => void;
  signOut: () => void;
  /** Wrap an admin API call: a server-side session rejection returns the user to sign-in. */
  guard: <T>(request: Promise<T>) => Promise<T>;
}

const AdminSessionContext = createContext<AdminSessionContextValue | null>(null);

export function useAdminSession(): AdminSessionContextValue {
  const ctx = useContext(AdminSessionContext);
  if (!ctx) throw new Error("useAdminSession must be used inside <AdminSessionGate>");
  return ctx;
}

// Route guard for /admin/* and /schedule. Shows the administrator sign-in until a live admin
// session exists, then renders the protected screen. UX only — the server rejects every
// admin/schedule API call without a valid session regardless of what this component renders.
export function AdminSessionGate({ children }: { children: ReactNode }) {
  const { t } = useI18n();
  const [session, setSession] = useState<AdminSession | null>(() => readAdminSession());
  const [notice, setNotice] = useState<string | null>(null);

  const expire = useCallback(() => {
    clearAdminSession();
    setSession(null);
    setNotice(t.auth.sessionExpired);
  }, [t]);

  // Client-side expiry: drop the session when its server-issued lifetime ends.
  useEffect(() => {
    if (!session) return;
    const id = window.setTimeout(expire, msUntilExpiry(session, Date.now()));
    return () => window.clearTimeout(id);
  }, [session, expire]);

  // Re-verify a restored session with the server (it may have been revoked by a password
  // change or logout elsewhere) and refresh the default-password flag.
  const token = session?.token;
  useEffect(() => {
    if (!session) return;
    let cancelled = false;
    fetchSessionStatus(session)
      .then((status) => {
        if (!cancelled && status.mustChangePassword !== session.mustChangePassword)
          setSession(storeAdminSession({ ...session, mustChangePassword: status.mustChangePassword }));
      })
      .catch((e: unknown) => { if (!cancelled && isSessionRejected(e)) expire(); });
    return () => { cancelled = true; };
    // Only when the token changes — not on the mustChangePassword refresh above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const value = useMemo<AdminSessionContextValue | null>(() => session && {
    session,
    replaceSession: setSession,
    signOut: () => {
      void signOut(session);
      setNotice(null);
      setSession(null);
    },
    guard: <T,>(request: Promise<T>) => request.catch((e: unknown) => {
      if (isSessionRejected(e)) expire();
      throw e;
    })
  }, [session, expire]);

  const gate = decideAdminGate({ session, nowMs: Date.now() });
  if (gate.kind === "login" || !value) {
    return <AdminLogin notice={notice} onSignedIn={(s) => { setNotice(null); setSession(s); }} />;
  }

  return (
    <AdminSessionContext.Provider value={value}>
      <AdminSessionBar mustChangePassword={gate.mustChangePassword} />
      {children}
    </AdminSessionContext.Provider>
  );
}

function AdminSessionBar({ mustChangePassword }: { mustChangePassword: boolean }) {
  const { t, dir } = useI18n();
  const { session, signOut: doSignOut } = useAdminSession();
  return (
    <div dir={dir} style={{ fontFamily: "Segoe UI, Arial, sans-serif", fontSize: 13 }}>
      {mustChangePassword && (
        <div role="alert" style={{
          background: "#fef3c7", color: "#78350f", borderBottom: "1px solid #fcd34d",
          padding: "8px 18px", display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap"
        }}>
          <span>{t.auth.defaultPasswordWarning}</span>
          <Link to="/admin/settings" style={{ color: "#78350f", fontWeight: 600 }}>{t.auth.goToSettings}</Link>
        </div>
      )}
      <div style={{
        display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 10,
        padding: "6px 18px", color: "#475569", background: "#f8fafc", borderBottom: "1px solid #e2e8f0"
      }}>
        <span>{t.auth.signedInAs} <strong dir="ltr">{session.displayName}</strong></span>
        <button type="button" onClick={doSignOut}
          style={{ background: "#e2e8f0", color: "#1e2a3a", border: "none", borderRadius: 6, padding: "4px 12px", cursor: "pointer" }}>
          {t.auth.signOut}
        </button>
      </div>
    </div>
  );
}
